import { driver } from "driver.js";
import { TOUR_STEPS_CONFIG } from "./tourSteps";
import EventBus from "@/plugins/EventBus";

const TOUR_COMPLETED_KEY = "my_happy_home_tour_completed";
const TOUR_STEP_INDEX_KEY = "my_happy_home_tour_step_index";
const TOUR_DUMMY_CHECKOUT_KEY = "my_happy_home_tour_dummy_checkout";

let activeDriver = null;
let frozenElement = null; // Track element with disabled interaction
let isTransitioning = false;

export const isTourActive = () => {
    return activeDriver !== null && document.documentElement.classList.contains('tour-active');
};

const closeAllModals = () => {
    const modals = document.querySelectorAll('.modal.show');
    modals.forEach(modal => {
        const closeBtn = modal.querySelector('[data-bs-dismiss="modal"]');
        if (closeBtn) closeBtn.click();
        else {
            modal.classList.remove('show');
            document.body.classList.remove('modal-open');
            const backdrop = document.querySelector('.modal-backdrop');
            if (backdrop) backdrop.remove();
        }
    });
};

const getCurrentPageType = (router) => {
    const path = router.currentRoute.value.path;
    if (path.includes('products')) return 'products';
    if (path.includes('dashboard')) return 'dashboard';
    if (path.includes('affiliates')) return 'affiliates';
    return 'other';
};

const isCheckoutBtnVisible = () => {
    const el = document.querySelector('#tour-continue-checkout');
    return !!(el && (el.offsetWidth > 0 || el.offsetHeight > 0));
};

const getStepId = (step) => {
    return TOUR_STEPS_CONFIG.findIndex(s =>
        s.element === step.element && s.popover?.title === step.popover?.title
    );
};

const preventKeyboard = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

const ensureMobileMenuOpen = async () => {
    const width = window.innerWidth || document.documentElement.clientWidth;

    if (width > 1024) return;

    // Force Open Menu: isHidden check is unreliable for opacity/max-height menus
    if (typeof window.forceOpenMobileMenu === 'function') {
        window.forceOpenMobileMenu();
    } else {
        EventBus.emit('openMobileMenu');
    }

    // Wait for it to become visible (max 4 seconds)
    let tries = 0;
    while (tries < 40) {
        await new Promise(r => setTimeout(r, 100));
        const el = document.querySelector('#tour-profile-pic');
        // Check if REALLY visible (in viewport and not display:none)
        const isActuallyVisible = !!(el && el.offsetParent !== null && el.getBoundingClientRect().height > 0);

        if (isActuallyVisible) {
            await new Promise(r => setTimeout(r, 400)); // Extra time for animation to finish
            break;
        }
        tries++;
    }
};

export const startTour = async (router, store) => {
    if (window.localStorage.getItem(TOUR_COMPLETED_KEY) === "true") return;

    // Destroy existing instance to ensure fresh start on route change
    if (activeDriver) {
        activeDriver.destroy();
        activeDriver = null;
    }

    const isReady = () => {
        const isLoading = store.getters.apiCallback;
        const currentPath = router.currentRoute.value.path;

        if (currentPath.includes('products')) {
            const hasProducts = store.getters.protectionPlanBundle && store.getters.protectionPlanBundle.length > 0;
            const el = document.querySelector('#tour-review-coverage') || document.querySelector('#tour-select-plan');
            return !isLoading && hasProducts && !!(el && el.offsetParent !== null);
        }

        if (currentPath.includes('affiliates')) {
            const hasData = store.getters.affiliateList && store.getters.affiliateList.body;
            const el = document.querySelector('#tour-add-affiliate');
            return !isLoading && hasData && !!(el && el.offsetParent !== null);
        }

        if (currentPath.includes('dashboard')) {
            const el = document.querySelector('#tour-logo');
            return !isLoading && !!(el && el.offsetParent !== null);
        }

        return !isLoading;
    };

    let tries = 0;
    while (!isReady() && tries < 60) {
        await new Promise(resolve => setTimeout(resolve, 200));
        tries++;
    }

    await new Promise(resolve => setTimeout(resolve, 500));

    let savedIndex = parseInt(localStorage.getItem(TOUR_STEP_INDEX_KEY) || "0");

    const getTargetStepIndex = (startIndex) => {
        const pageCategory = getCurrentPageType(router);
        const checkoutFound = isCheckoutBtnVisible();
        const selectPlanIdx = TOUR_STEPS_CONFIG.findIndex(s => s.element === '#tour-select-plan');

        const isBypassed = (idx) => idx === selectPlanIdx && checkoutFound;

        const checkIndex = (i) => {
            const step = TOUR_STEPS_CONFIG[i];

            // If checking #tour-affiliates and it's missing, ensure we don't accidentally match sub-steps if they were somehow valid
            // But main check is pageCategory and element existence

            if (step.page === pageCategory && !isBypassed(i)) {
                if (!step.element) return true;
                const el = document.querySelector(step.element);

                // Special case for profile pic on mobile: it's inside the collapsed menu
                const isMobile = window.innerWidth <= 1024;
                if (step.element === '#tour-profile-pic' && isMobile) {
                    return !!el;
                }

                return !!(el && el.offsetParent !== null);
            }
            return false;
        };

        if (startIndex >= 0 && startIndex < TOUR_STEPS_CONFIG.length && checkIndex(startIndex)) return startIndex;

        // Search FORWARD first as that's the natural flow
        for (let i = startIndex + 1; i < TOUR_STEPS_CONFIG.length; i++) if (checkIndex(i)) return i;
        for (let i = startIndex - 1; i >= 0; i--) if (checkIndex(i)) return i;

        return -1;
    };

    const startIndex = getTargetStepIndex(savedIndex);
    if (startIndex === -1) {
        document.documentElement.classList.remove('tour-active');
        document.body.classList.remove('tour-active');
        return;
    }

    const startStep = TOUR_STEPS_CONFIG[startIndex];

    // ONLY apply classes here when we are SURE the tour is ready to display a step
    document.documentElement.classList.add('tour-active');
    document.body.classList.add('tour-active');
    isTransitioning = false;
    EventBus.emit('tourDummyTrigger'); // Notify components that class might have changed

    if (startStep && startStep.element === '#tour-profile-pic') {
        await ensureMobileMenuOpen();
    }

    activeDriver = driver({
        allowKeyboardControl: false, // Ensure driver doesn't use keyboard

        showProgress: false,
        animate: true,
        overlayColor: 'rgba(0, 0, 0, 0.7)',
        stagePadding: 10,
        allowClose: false,
        overlayClickAction: 'none',
        keyboardControl: false,
        popoverClass: 'tour-modern-popover',
        nextBtnText: 'Next',
        prevBtnText: 'Skip & Exit',
        steps: TOUR_STEPS_CONFIG,
        onHighlightStarted: (element, step) => {
            // Prevent keyboard interaction
            window.addEventListener('keydown', preventKeyboard, true);

            // Clean up previous element's frozen state if needed
            if (frozenElement && frozenElement !== element && frozenElement.style) {
                frozenElement.style.pointerEvents = '';
            }
            frozenElement = element;

            if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
            if (element && typeof element.blur === 'function') {
                element.blur();
                element.style.pointerEvents = 'none'; // Prevent manual clicks/focus
            }

            document.documentElement.classList.add('tour-active');
            document.body.classList.add('tour-active');
            const idx = getStepId(step);
            localStorage.setItem(TOUR_STEP_INDEX_KEY, idx.toString());

            // Handle mobile menu for profile pic
            if (step.element === '#tour-profile-pic' && window.innerWidth <= 1024) {
                ensureMobileMenuOpen();

                let start = Date.now();
                const syncPos = () => {
                    if (activeDriver) activeDriver.refresh();
                    if (Date.now() - start < 2000) {
                        requestAnimationFrame(syncPos);
                    }
                };
                requestAnimationFrame(syncPos);
            }

            if (step.action === 'save_affiliate') {
                const modal = document.querySelector('#addListModal');
                if (!modal || !modal.classList.contains('show')) {
                    document.querySelector('#tour-add-affiliate')?.click();
                    setTimeout(() => { if (activeDriver) activeDriver.refresh(); }, 1000);
                }
            } else if (step.element === '#tour-copy-url' || step.action === 'open_profile') {
                // Keep modals open
            } else {
                closeAllModals();
            }
        },
        onHighlighted: (element, step) => {
            const idx = getStepId(step);
            const isLastStep = idx === TOUR_STEPS_CONFIG.length - 1;

            if (!isLastStep) {
                let checkCount = 0;
                const injectInterval = setInterval(() => {
                    const popover = document.querySelector('.driver-popover.tour-modern-popover');
                    if (popover) {
                        if (!popover.querySelector('.custom-tour-close-btn')) {
                            const closeBtn = document.createElement('div');
                            closeBtn.className = 'custom-tour-close-btn';

                            const img = document.createElement('img');
                            img.src = require('@/assets/images/modal-cross-icon.svg');
                            img.style.width = '80%';
                            img.style.height = '80%';
                            closeBtn.appendChild(img);

                            closeBtn.title = 'Exit Tour';
                            closeBtn.onclick = () => {
                                if (activeDriver) activeDriver.destroy();
                                document.documentElement.classList.remove('tour-active');
                                document.body.classList.remove('tour-active');

                                EventBus.emit("confirmDialogShow", {
                                    title: "Exit Tour",
                                    text: "Are you sure you want to exit the tour?",
                                    onConfirm: () => {
                                        window.localStorage.setItem(TOUR_COMPLETED_KEY, "true");
                                        closeAllModals();
                                        activeDriver = null;
                                    },
                                    onClose: () => {
                                        localStorage.setItem(TOUR_STEP_INDEX_KEY, idx.toString());
                                        startTour(router, store);
                                    }
                                });
                            };
                            popover.appendChild(closeBtn);
                        }
                        clearInterval(injectInterval);
                    }
                    if (++checkCount > 20) clearInterval(injectInterval);
                }, 100);
            }
        },
        onNextClick: async (element, step) => {
            const currentIdx = getStepId(step);
            const selectPlanIdx = TOUR_STEPS_CONFIG.findIndex(s => s.element === '#tour-select-plan');
            const checkoutIdx = TOUR_STEPS_CONFIG.findIndex(s => s.element === '#tour-continue-checkout');

            if (step.element === '#tour-profile-pic') {
                let isEmployee = false;
                try {
                    const user = JSON.parse(localStorage.getItem('user'));
                    if (user?.user?.affiliate?.affiliate_type === 'employee') {
                        isEmployee = true;
                    }
                } catch (e) {
                    // console.error(e);
                }

                if (isEmployee) {
                    const completedStepIdx = TOUR_STEPS_CONFIG.findIndex(s => s.popover?.title === 'Tour Completed');
                    if (completedStepIdx !== -1) {
                        localStorage.setItem(TOUR_STEP_INDEX_KEY, completedStepIdx.toString());
                        if (activeDriver) activeDriver.destroy();
                        startTour(router, store);
                        return;
                    }
                }

                isTransitioning = true;
                if (activeDriver) activeDriver.destroy();
                document.querySelector('#tour-profile-pic')?.click();

                let tries = 0;
                while (tries < 40) {
                    await new Promise(r => setTimeout(r, 100));
                    const nextEl = document.querySelector('#tour-copy-url');
                    const modal = document.querySelector('#profileModal');
                    if (nextEl && nextEl.offsetParent !== null && modal && modal.classList.contains('show')) break;
                    tries++;
                }

                localStorage.setItem(TOUR_STEP_INDEX_KEY, (currentIdx + 1).toString());
                startTour(router, store);
                return;
            }

            if (step.element === '#tour-add-affiliate') {
                isTransitioning = true;
                if (activeDriver) activeDriver.destroy();
                document.querySelector('#tour-add-affiliate')?.click();

                let tries = 0;
                while (tries < 60) {
                    await new Promise(r => setTimeout(r, 200));
                    const nextEl = document.querySelector('#tour-save-affiliate');
                    if (nextEl && nextEl.offsetParent !== null) break;
                    tries++;
                }

                localStorage.setItem(TOUR_STEP_INDEX_KEY, (currentIdx + 1).toString());
                startTour(router, store);
                return;
            }

            if (step.element === '#tour-save-affiliate') {
                localStorage.setItem(TOUR_STEP_INDEX_KEY, (currentIdx + 1).toString());
                closeAllModals();
                router.push('/dashboard');
                if (activeDriver) activeDriver.destroy();
                return;
            }

            if (step.element === '#tour-select-plan') {
                isTransitioning = true;
                if (activeDriver) activeDriver.destroy();

                // Instead of real click/API, set dummy flag
                localStorage.setItem(TOUR_DUMMY_CHECKOUT_KEY, "true");
                EventBus.emit('tourDummyTrigger');

                let tries = 0;
                while (tries < 40) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    if (isCheckoutBtnVisible()) break;
                    tries++;
                }

                localStorage.setItem(TOUR_STEP_INDEX_KEY, (currentIdx + 1).toString());
                startTour(router, store);
                return;
            }

            // Find next available step
            const findNextAvailableStep = (fromIdx) => {
                const currentPage = getCurrentPageType(router);

                for (let i = fromIdx; i < TOUR_STEPS_CONFIG.length; i++) {
                    const step = TOUR_STEPS_CONFIG[i];

                    if (i === selectPlanIdx && isCheckoutBtnVisible()) continue;

                    if (!step.element) return i;

                    const el = document.querySelector(step.element);

                    // Skip affiliate block if #tour-affiliates is explicitly missing on current page
                    if (step.element === '#tour-affiliates' && currentPage === 'dashboard' && (!el || !el.offsetParent)) {
                        // User wants to skip #tour-add-affiliate and #tour-save-affiliate as well
                        // Identify indices to skip
                        // We are at loop i. Next steps i+1, i+2 might be affiliates.
                        // Just continue loop, they will be handled by regular logic or we can jump?
                        // Continuing loop is safe: 'affiliates' page steps will be skipped by page check below?
                        // Actually if we are on dashboard, page check below skips them anyway.
                        // So checking #tour-affiliates missing is enough.
                        continue;
                    }

                    // If step is on another page, RETURN IT (so we navigate there)
                    if (step.page !== currentPage) return i;

                    // If step is on SAME page, it MUST exist
                    const isMobile = window.innerWidth <= 1024;
                    if (step.element === '#tour-profile-pic' && isMobile) {
                        if (el) return i;
                        continue;
                    }

                    if (el && el.offsetParent !== null) return i;
                }
                return -1;
            };

            let nextIdx = findNextAvailableStep(currentIdx + 1);

            if (nextIdx === -1 || nextIdx >= TOUR_STEPS_CONFIG.length) {
                window.localStorage.setItem(TOUR_COMPLETED_KEY, "true");
                closeAllModals();
                if (activeDriver) activeDriver.destroy();
                return;
            }

            localStorage.setItem(TOUR_STEP_INDEX_KEY, nextIdx.toString());
            const nextStep = TOUR_STEPS_CONFIG[nextIdx];

            if (nextStep.element === '#tour-profile-pic') {
                await ensureMobileMenuOpen();
            }

            if (nextStep.page !== getCurrentPageType(router)) {
                closeAllModals();
                const pathMap = { 'products': '/protection-plan/myhappyhome/products', 'dashboard': '/dashboard', 'affiliates': '/affiliates' };
                if (pathMap[nextStep.page]) router.push(pathMap[nextStep.page]);

                if (activeDriver) activeDriver.destroy();
                return;
            }

            if (activeDriver) activeDriver.drive(nextIdx);
        },
        onDestroyed: () => {
            if (!isTransitioning) {
                if (frozenElement && frozenElement.style) frozenElement.style.pointerEvents = '';
                frozenElement = null;
                window.removeEventListener('keydown', preventKeyboard, true);
                document.documentElement.classList.remove('tour-active');
                document.body.classList.remove('tour-active');
                localStorage.removeItem(TOUR_DUMMY_CHECKOUT_KEY);
                EventBus.emit('tourDummyTrigger');
                activeDriver = null;
            }
        }
    });

    activeDriver.drive(startIndex);
};

export const resetTour = () => {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
    localStorage.setItem(TOUR_STEP_INDEX_KEY, "0");
    localStorage.removeItem(TOUR_DUMMY_CHECKOUT_KEY);
    EventBus.emit('tourDummyTrigger');
    document.documentElement.classList.remove('tour-active');
    document.body.classList.remove('tour-active');
    closeAllModals();
    if (activeDriver) activeDriver.destroy();
};
